// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions, { ProductActionsStyle } from "@guestsupply/App/Components/Product/ProductActions";
import ProductDiscontinuedMessage from "@guestsupply/App/Components/Product/ProductDiscontinuedMessage";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@guestsupply/App/Components/Product/ProductPriceAndActions";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = HasProductContext;

export interface ProductDetailsSummaryStyles {
    container?: InjectableCss;
    rightSection?: InjectableCss;
    leftSection?: InjectableCss;
    productImageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    descriptionWrapper?: InjectableCss;
    productTitle?: TypographyPresentationProps;
    productActionsExtendedStyles?: ProductActionsStyle;
    minQtyWrapper?: InjectableCss;
    itemReplacementsWrapper?: InjectableCss;
    priceAndActionStyles?: ProductPriceAndActionsStyle;
}

export const productDetailsSummaryStyles: ProductDetailsSummaryStyles = {};

const styles = productDetailsSummaryStyles;

const ProductDetailsSummary: FC<Props> = ({ productContext }) => {
    const { product } = productContext;
    const { selectedImage, notFoundImage, productImage, canSeePrices } = useAppSelector(state => {
        const { notFoundSmallImagePath, notFoundMediumImagePath, notFoundLargeImagePath } =
            getSettingsCollection(state).customSettings;
        return {
            selectedImage: getSelectedImage(state, product),
            notFoundImage: notFoundSmallImagePath || notFoundMediumImagePath || notFoundLargeImagePath,
            productImage: product.smallImagePath || product.mediumImagePath || product.largeImagePath,
            canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
        };
    });

    if (!product) {
        return null;
    }

    const path =
        selectedImage?.mediumImagePath ||
        selectedImage?.smallImagePath ||
        selectedImage?.largeImagePath ||
        productImage ||
        notFoundImage;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftSection}>
                <StyledWrapper {...styles.productImageWrapper}>
                    <LazyImage
                        src={path}
                        altText={product.imageAltText || product.productTitle}
                        {...styles.productImage}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.descriptionWrapper}>
                    <Typography {...styles.productTitle}>{product.productTitle}</Typography>
                    <ProductContextAvailability />
                    <StyledWrapper {...styles.itemReplacementsWrapper}>
                        {createWidgetElement("ProductDetails/ProductDetailsItemReplacements", {
                            fields: {},
                        })}
                    </StyledWrapper>
                    <ProductDiscontinuedMessage />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <ProductPriceAndActions
                    showAddToList={false}
                    showAvailability={false}
                    showPrice={canSeePrices}
                    showAddToCartActions={true}
                    iconOnly={true}
                    showDisabled={true}
                    displayAutoReorderAsButton={false}
                    showAutoDelivery={false}
                    isPdp={true}
                    extendedStyles={styles.priceAndActionStyles}
                />
                <StyledWrapper {...styles.minQtyWrapper}>
                    {createWidgetElement("ProductDetails/ProductDetailsMinQty", {
                        fields: {},
                    })}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductDetailsSummary);
